// Bin money numbers from system_config, for the ACTIVE tenant.
//
// Five inline copies of these reads lived across Tipping, Rebin, and the three
// bin-inventory tabs. Each filtered by config_key ALONE and let RLS scope the
// row to the tenant — the pattern mig 696 deliberately sanctioned, because it
// fixed every call site at once with no client change.
//
// It holds for every ordinary user, including a member of several tenants. It
// does NOT hold for a platform admin: is_platform_admin() satisfies the
// tenant_gate policy for EVERY row, so `.maybeSingle()` throws PGRST116 and
// `.in()` returns one row PER TENANT.
//
// The old call sites all discarded `error` and fell back to a default, so the
// failure was silent and confident rather than loud:
//
//   - bin tare → 25 kg. Tare sets every tipped bin's NET weight, and net weight
//     is what the grower is paid on.
//   - fleet total / daily rental → 0. Bin rental bills off these.
//   - repair / replacement cost → 0, so recorded damage recovers nothing.
//
// Hence `isFallback` on each hook: a screen showing a number that came from a
// failed read should be able to say so instead of presenting 0 as fact.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'

export const DEFAULT_BIN_TARE_KG = 25

export interface ConfigRow { config_key: string; config_value: string }

/** Rows → number for one key. Exported for the tests; no I/O. */
export function numFromRows(rows: ConfigRow[] | undefined, key: string, fallback: number): number {
  const raw = rows?.find(r => r.config_key === key)?.config_value
  if (raw == null || raw === '') return fallback
  const n = parseFloat(raw)
  return Number.isFinite(n) ? n : fallback
}

function useBinConfigKeys(queryKey: string, keys: string[]) {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const q = useQuery({
    queryKey: [queryKey, tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_config')
        .select('config_key, config_value')
        .eq('tenant_id', tenantId!)
        .in('config_key', keys)
      if (error) throw error
      return (data ?? []) as ConfigRow[]
    },
  })
  // `data !== undefined` is the discriminator, not isSuccess: react-query v5
  // reports status 'error' on a failed REFETCH while the previous rows are
  // still in hand, and those rows are still the tenant's real figures.
  return { rows: q.data, isFallback: q.data === undefined }
}

/** Bin tare in kg — drives every tipped bin's net weight. */
export function useBinTare(): { tareKg: number; isFallback: boolean } {
  const { rows, isFallback } = useBinConfigKeys('bin-tare', ['bin_tare_kg'])
  return { tareKg: numFromRows(rows, 'bin_tare_kg', DEFAULT_BIN_TARE_KG), isFallback }
}

/** The line beside a net weight priced against the DEFAULT tare. `isFallback`
 *  is true while the read is IN FLIGHT as well as after it failed, so the
 *  sentence must be true for both: it says the tare "hasn't loaded", never
 *  that it couldn't be read — on a normal page load that would be a false
 *  claim of a failure. "If this line stays" is the failed case's next step. */
export function binTareFallbackNote(screen: 'tipping' | 'rebin'): string {
  return screen === 'tipping'
    ? `The bin tare hasn't loaded, so this uses the default ${DEFAULT_BIN_TARE_KG} kg and the net weight may be wrong. If this line stays, reload before tipping.`
    : `The bin tare hasn't loaded, so these use the default ${DEFAULT_BIN_TARE_KG} kg and the net weights below may be wrong. If this line stays, reload before saving.`
}

/** Fleet size + daily rental — what bin rental is billed off. */
export function useBinFleetConfig(): { fleetTotal: number; dailyRental: number; isFallback: boolean } {
  const { rows, isFallback } = useBinConfigKeys('bin-fleet-config', ['bin_fleet_total', 'bin_daily_rental_zar'])
  return {
    fleetTotal:  Math.trunc(numFromRows(rows, 'bin_fleet_total', 0)) || 0,
    dailyRental: numFromRows(rows, 'bin_daily_rental_zar', 0),
    isFallback,
  }
}

/** Repair / replacement costs charged for damaged bins. */
export function useBinDamageRates(): { repair: number; replace: number; isFallback: boolean } {
  const { rows, isFallback } = useBinConfigKeys('bin-damage-rates', ['bin_repair_cost_zar', 'bin_replacement_cost_zar'])
  return {
    repair:  numFromRows(rows, 'bin_repair_cost_zar', 0),
    replace: numFromRows(rows, 'bin_replacement_cost_zar', 0),
    isFallback,
  }
}
